import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Key, Plus, Trash2, Copy, Lock, AlertTriangle } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function ApiKeys() {
  const tr = useTr();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [justCreated, setJustCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gated, setGated] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/v1/keys");
      setKeys(data);
      setGated(false);
    } catch (e) {
      if (e.response?.status === 402) setGated(true);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error(tr("Nómbrala para identificarla después"));
    setBusy(true);
    try {
      const { data } = await api.post("/v1/keys", { name });
      setJustCreated(data);
      setName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta key permanentemente?")) return;
    try {
      await api.delete(`/v1/keys/${id}`);
      toast.success("Key revocada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado");
  };

  if (gated) {
    return (
      <div className="bg-white border border-[#0A0A0A] p-10 text-center max-w-xl" data-testid="api-keys-gated">
        <Lock className="w-12 h-12 mx-auto text-[#5C5C5C]" />
        <h1 className="font-display text-3xl mt-4">{tr("API pública · solo Agencia")}</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C]">
          {tr("La API REST (")}<code>/api/v1/public</code>) está disponible en plan Agencia.
          Conecta tu automatización externa — Zapier, Make, n8n o tu propio backend.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="api-keys-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">DOSSIER · API · V1</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">API Keys</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C] max-w-xl">
          {tr("Genera keys para conectar integraciones externas. Cabecera:")} <code className="bg-[#F4F4F0] px-1">X-CIA-API-Key: cia_...</code>
        </p>
      </div>

      {/* New key form */}
      <div className="bg-white border border-[#0A0A0A] p-5 flex gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={tr("Ej: Zapier producción")}
          className="flex-1 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
          data-testid="api-key-name-input"
        />
        <button onClick={create} disabled={busy}
          className="bg-[#0A0A0A] text-white px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover-brutal flex items-center gap-2 disabled:opacity-50"
          data-testid="api-key-create">
          <Plus className="w-4 h-4" /> {busy ? tr("CREANDO…") : tr("GENERAR KEY")}
        </button>
      </div>

      {/* Newly created secret (shown once) */}
      {justCreated && (
        <div className="bg-[#FFD600] border border-[#0A0A0A] p-5 space-y-3" data-testid="api-key-new">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-mono-mark text-[10px]">{tr("COPIA ESTA CLAVE AHORA — NO SE VOLVERÁ A MOSTRAR")}</span>
          </div>
          <div className="flex gap-2 items-center bg-white border border-[#0A0A0A] p-3">
            <code className="font-mono-data text-xs flex-1 break-all select-all">{justCreated.secret}</code>
            <button onClick={() => copy(justCreated.secret)} className="bg-[#0A0A0A] text-white px-3 py-2 font-mono-mark text-[9px]" data-testid="api-key-copy">
              <Copy className="w-3 h-3 inline mr-1" /> {tr("COPIAR")}
            </button>
          </div>
          <button onClick={() => setJustCreated(null)} className="font-mono-mark text-[10px] underline">
            {tr("OK, ya la guardé")}
          </button>
        </div>
      )}

      {/* Keys list */}
      <div className="bg-white border border-[#0A0A0A]">
        <div className="px-5 py-3 border-b border-[#0A0A0A] font-mono-mark text-[10px] text-[#5C5C5C]">KEYS ACTIVAS · {keys.filter(k => !k.revoked).length}</div>
        {keys.length === 0 ? (
          <div className="p-10 text-center font-mono-data text-sm text-[#5C5C5C]">
            {tr("No has creado ninguna key todavía.")}
          </div>
        ) : (
          <ul>
            {keys.map((k) => (
              <li key={k.id} className="px-5 py-4 border-b border-[#0A0A0A]/20 flex items-center justify-between flex-wrap gap-3" data-testid={`api-key-${k.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    <span className="font-display text-base truncate">{k.name}</span>
                    {k.revoked && <span className="font-mono-mark text-[9px] bg-[#E53935] text-white px-2 py-0.5">REVOCADA</span>}
                  </div>
                  <div className="font-mono-data text-xs text-[#5C5C5C] mt-1">
                    <code>{k.prefix}…</code> · {tr("creada")} {new Date(k.created_at).toLocaleDateString("en-US")}
                    {k.last_used_at && ` · ${tr("último uso")} ${new Date(k.last_used_at).toLocaleString("en-US")}`}
                  </div>
                </div>
                {!k.revoked && (
                  <button onClick={() => revoke(k.id)} className="font-mono-mark text-[10px] border border-[#E53935] text-[#E53935] px-3 py-2 flex items-center gap-1" data-testid={`api-key-revoke-${k.id}`}>
                    <Trash2 className="w-3 h-3" /> {tr("REVOCAR")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Docs */}
      <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] p-5 font-mono-data text-xs space-y-3">
        <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("DOCUMENTACIÓN · ENDPOINTS PÚBLICOS")}</div>
        <pre className="whitespace-pre-wrap text-[11px] leading-relaxed">
{`GET  /api/v1/public/me         → { id, email, plan }
GET  /api/v1/public/posts      → { count, items:[...] }
GET  /api/v1/public/analytics  → { posts_total, posts_published }

Ejemplo:
curl -H "X-CIA-API-Key: cia_XXXX" \\
     https://api.ciamarketing.com/api/v1/public/posts`}
        </pre>
      </div>
    </div>
  );
}
